import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { waLink } from "../../lib/constants";
import { X, Send } from "lucide-react";
import whatsappAnimation from "../../assets/whatsapp-button.json";

const WaGlyph = ({ size = 52 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="waFabGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#5BD066" />
        <stop offset="100%" stopColor="#27B43E" />
      </linearGradient>
    </defs>
    <path
      fill="url(#waFabGrad)"
      d="M16 2C8.3 2 2 8.1 2 15.6c0 2.6.7 5 2 7.1L2 30l7.6-2c2 1.1 4.2 1.7 6.4 1.7 7.7 0 14-6.1 14-13.6C30 8.1 23.7 2 16 2z"
    />
    <path
      fill="#fff"
      d="M21.6 18.9c-.4-.2-2.2-1.1-2.6-1.2-.3-.1-.6-.2-.9.2-.3.4-1 1.2-1.2 1.4-.2.3-.4.3-.8.1-.4-.2-1.6-.6-3-1.9-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8.2-.2.4-.4.5-.6.2-.2.2-.4.3-.6.1-.2.1-.5 0-.7-.1-.2-.9-2.2-1.3-3-.3-.8-.7-.7-.9-.7h-.8c-.3 0-.7.1-1 .5-.4.4-1.4 1.3-1.4 3.2 0 1.9 1.4 3.7 1.6 4 .2.3 2.8 4.3 6.8 6 .9.4 1.7.6 2.3.8 1 .3 1.8.2 2.5.1.8-.1 2.2-.9 2.5-1.8.3-.9.3-1.6.2-1.8-.1-.2-.4-.3-.8-.5z"
    />
  </svg>
);


/**
 * The Lottie button. lottie-web is loaded lazily so it never blocks first
 * paint, and if it fails to load for any reason the static SVG below is used
 * instead — the button must always work, animation or not.
 */
function LottieButton({ size = 56 }) {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let anim;
    let cancelled = false;
    import("lottie-web")
      .then((mod) => {
        if (cancelled || !hostRef.current) return;
        const lottie = mod.default || mod;
        anim = lottie.loadAnimation({
          container: hostRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          animationData: whatsappAnimation,
        });
      })
      .catch(() => !cancelled && setFailed(true));
    return () => { cancelled = true; if (anim) anim.destroy(); };
  }, []);

  if (failed) return <WaGlyph size={52} />;
  return <div ref={hostRef} style={{ width: size, height: size }} aria-hidden="true" />;
}


/**
 * WhatsApp chat bubble.
 *
 * Tapping it opens a short list of ready-made questions so a visitor can start
 * a conversation in one tap, plus a free-text box for anything else. The list
 * is managed by Super Admin / Major Admin from the dashboard.
 */
export default function WhatsAppFab() {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [own, setOwn] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/api/wa-questions")
      .then((r) => setQuestions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuestions([]));
  }, []);

  // Close when clicking away
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const send = (message) => {
    const text = (message || "").trim();
    if (!text) return;
    window.open(waLink(text), "_blank", "noopener");
    setOpen(false);
    setOwn("");
  };

  return (
    <div ref={boxRef}>
      {open && (
        <div
          className="fixed bottom-24 right-6 sm:right-10 z-[70] w-[19rem] max-w-[calc(100vw-3rem)] bg-white border border-line shadow-2xl rounded-xl overflow-hidden"
          data-testid="wa-panel"
        >
          <div className="bg-[#128C7E] text-white px-4 py-3 flex items-start justify-between gap-2">
            <div>
              <div className="font-display text-lg leading-tight">Swayambhu Gold</div>
              <div className="text-[11px] opacity-90">Typically replies within a few minutes</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="opacity-80 hover:opacity-100 mt-0.5">
              <X size={16}/>
            </button>
          </div>

          <div className="p-3 max-h-80 overflow-y-auto bg-[#ECE5DD]">
            <div className="bg-white rounded-lg px-3 py-2 text-sm text-ink shadow-sm mb-3">
              Namaste. How can we help you today?
            </div>

            <div className="space-y-2">
              {questions.map((q) => (
                <button
                  key={q.id}
                  onClick={() => send(q.message || q.label)}
                  className="w-full text-left bg-white hover:bg-pearl border border-line rounded-lg px-3 py-2 text-sm text-ink transition-colors"
                  data-testid={`wa-q-${q.id}`}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* Anything else — free text */}
          <div className="p-3 border-t border-line bg-white">
            <div className="text-[10px] text-ash uppercase tracking-widest mb-1">Other question</div>
            <div className="flex gap-2">
              <input
                value={own}
                onChange={(e) => setOwn(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send(own)}
                placeholder="Type your question..."
                className="flex-1 border border-line px-3 py-2 text-sm bg-pearl rounded"
                data-testid="wa-own-question"
              />
              <button
                onClick={() => send(own)}
                disabled={!own.trim()}
                className="bg-[#25D366] text-white px-3 rounded disabled:opacity-40"
                aria-label="Send on WhatsApp"
                data-testid="wa-own-send"
              >
                <Send size={16}/>
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="wa-fab"
        data-testid="whatsapp-fab"
        aria-label={open ? "Close WhatsApp chat" : "Chat on WhatsApp"}
        aria-expanded={open}
      >
        <LottieButton />
      </button>
    </div>
  );
}
